import React, { useContext, useState } from 'react';
import { ProjectType, StateContext } from '../../StateContext';
import {
    Modal,
    Button,
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { boxStyle } from './styles';
import { editProjectCall, addProjectTypeRaw, deleteProjectTypeRaw, updateProjectTypeRaw } from '../../Services/APICalls/apicalls';
import { hourToSeconds } from '../../General/Links/addWeekOverview';
import { secondsToHours } from '../../General/Links/weekView';
import { useLanguage } from '../../Services/LanguageContext';

const PROJECT_TYPE_OPTIONS = ['Badkamer', 'Keuken', 'Toilet', 'Divers'];

interface TypeState {
    checked: boolean;
    hours: string;
}

interface PProps {
    about: string;
    name: string;
    id: number;
    estimatedTimeInSeconds: number;
    materialsDelivered: boolean;
    types?: ProjectType[];
}

const initTypeState = (existingTypes: ProjectType[]): Record<string, TypeState> => {
    return Object.fromEntries(PROJECT_TYPE_OPTIONS.map(name => {
        const found = existingTypes.find(t => t.name === name);
        return [name, found
            ? { checked: true, hours: String(secondsToHours(found.calculatedTimeInSeconds)) }
            : { checked: false, hours: '0' }
        ];
    }));
};

const EditProject = (props: PProps) => {
    const [projectName, setProjectName] = useState(props.name);
    const [description, setDescription] = useState(props.about);
    const [materialsDelivered, setMaterialsDelivered] = useState(props.materialsDelivered);
    const [typeState, setTypeState] = useState<Record<string, TypeState>>(initTypeState(props.types ?? []));
    const [open, setOpen] = useState(false);
    const { t } = useLanguage();

    const { loginData, setLoginData } = useContext<any>(StateContext);

    const editProjectHandler = async () => {
        const estimatedTimeInSeconds = props.estimatedTimeInSeconds;
        const existingTypes = props.types ?? [];

        const toAdd = PROJECT_TYPE_OPTIONS
            .filter(name => typeState[name].checked && !existingTypes.find(t => t.name === name))
            .map(name => ({
                name,
                calculatedTimeInSeconds: hourToSeconds(parseFloat(typeState[name].hours) || 0),
            }));

        const toRemove = existingTypes.filter(et => !typeState[et.name]?.checked);

        const toUpdate = existingTypes.filter(et => {
            if (!typeState[et.name]?.checked) return false;
            const newSeconds = hourToSeconds(parseFloat(typeState[et.name].hours) || 0);
            return newSeconds !== et.calculatedTimeInSeconds;
        });

        await Promise.all([
            ...toAdd.map(t => addProjectTypeRaw(props.id, t.name, t.calculatedTimeInSeconds)),
            ...toRemove.map(et => deleteProjectTypeRaw(props.id, et.id)),
            ...toUpdate.map(et => updateProjectTypeRaw(
                props.id,
                et.id,
                et.name,
                hourToSeconds(parseFloat(typeState[et.name].hours) || 0)
            )),
        ]);

        editProjectCall(props.id, projectName, estimatedTimeInSeconds, description, setLoginData, materialsDelivered);
        setOpen(false);
    };

    const handleOpen = () => {
        setProjectName(props.name);
        setDescription(props.about);
        setMaterialsDelivered(props.materialsDelivered);
        setTypeState(initTypeState(props.types ?? []));
        setOpen(true);
    };

    const toggleType = (name: string, checked: boolean) =>
        setTypeState(prev => ({ ...prev, [name]: { ...prev[name], checked } }));

    const setTypeHours = (name: string, hours: string) =>
        setTypeState(prev => ({ ...prev, [name]: { ...prev[name], hours: hours.replace(',', '.') } }));

    const inputStyle = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            background: 'var(--bg-tertiary)',
            '& fieldset': { borderColor: 'var(--border-color)' },
            '&:hover fieldset': { borderColor: '#00d9ff' },
            '&.Mui-focused fieldset': {
                borderColor: '#00d9ff',
                boxShadow: '0 0 10px rgba(0, 217, 255, 0.2)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: '#00d9ff' },
        },
        '& .MuiOutlinedInput-input': { color: 'var(--text-primary)' },
    };

    const hoursInputStyle = {
        width: '120px',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            background: 'var(--bg-tertiary)',
            '& fieldset': { borderColor: 'var(--border-color)' },
            '&:hover fieldset': { borderColor: '#00d9ff' },
            '&.Mui-focused fieldset': { borderColor: '#00d9ff' },
        },
        '& .MuiInputLabel-root': {
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: '#00d9ff' },
        },
        '& .MuiOutlinedInput-input': { color: 'var(--text-primary)' },
    };

    return (
        <React.Fragment>
            <Button variant='outlined' onClick={handleOpen}>
                {t('edit')}
            </Button>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby='modal-modal-title'
                aria-describedby='modal-modal-description'
            >
                <Box style={boxStyle as any}>
                    <form
                        onSubmit={(e) => { e.preventDefault(); editProjectHandler(); }}
                        action='#'
                        className='flex flex-col items-center gap-3'
                        style={{ width: '100%' }}
                    >
                        <TextField
                            type='text'
                            variant='outlined'
                            label={t('project')}
                            value={projectName}
                            fullWidth
                            onClick={(x) => x.stopPropagation()}
                            onChange={(x) => setProjectName(x.target.value)}
                        />
                        <div style={{ width: '100%' }}>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px' }}>
                                {t('projectTypes')}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {PROJECT_TYPE_OPTIONS.map(name => (
                                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={typeState[name].checked}
                                                    onChange={(e) => toggleType(name, e.target.checked)}
                                                    sx={{
                                                        color: 'var(--text-secondary)',
                                                        '&.Mui-checked': { color: '#00d9ff' },
                                                    }}
                                                />
                                            }
                                            label={name}
                                            sx={{ color: 'var(--text-primary)', margin: 0, minWidth: '120px' }}
                                        />
                                        {typeState[name].checked && (
                                            <TextField
                                                size='small'
                                                type='text'
                                                label={t('calculatedHours')}
                                                value={typeState[name].hours}
                                                inputProps={{ maxLength: 10 }}
                                                onChange={(e) => setTypeHours(name, e.target.value)}
                                                sx={hoursInputStyle}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <TextField
                            id='filled-multiline-static'
                            label={t('remarks')}
                            multiline
                            rows={4}
                            variant='outlined'
                            value={description}
                            fullWidth
                            onClick={(x) => x.stopPropagation()}
                            onChange={(x) => setDescription(x.target.value)}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={materialsDelivered}
                                    onChange={(e) => setMaterialsDelivered(e.target.checked)}
                                    sx={{
                                        color: 'var(--text-secondary)',
                                        '&.Mui-checked': {
                                            color: '#00d9ff',
                                        },
                                    }}
                                />
                            }
                            label={t('materialsDelivered')}
                            sx={{
                                color: 'var(--text-primary)',
                                width: '100%',
                            }}
                        />
                        <button style={{ width: '100%' }}>{t('edit')}</button>
                    </form>
                </Box>
            </Modal>
        </React.Fragment>
    );
};

export default EditProject;
